import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LetterGenerator() {
  const [relationship, setRelationship] = useState('');
  const [tone, setTone] = useState('');
  const [occasion, setOccasion] = useState('');
  const [lifeUpdates, setLifeUpdates] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedLetter, setGeneratedLetter] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedLetter('');

    try {
      const response = await fetch(`${API}/letters/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          relationship_type: relationship,
          tone,
          occasion,
          life_updates: lifeUpdates,
          custom_prompt: customPrompt
        })
      });

      if (!response.ok) {
        const err = await response.json();
        toast.error(err.detail || 'Failed to generate letter');
        setGenerating(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              if (parsed.letter_id) {
                toast.success('Letter draft saved!');
              } else if (parsed.error) {
                toast.error(parsed.error);
              }
            } catch {
              setGeneratedLetter(prev => prev + data);
            }
          }
        }
      }
    } catch (error) {
      toast.error('Failed to generate letter');
    }
    setGenerating(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Write a Letter</h1>
          <p className="text-base text-[#737373]">Choose relationship and tone. Add details. AI creates a draft you can edit.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <h3 className="text-xl mb-6" style={{fontFamily: 'Spectral, serif'}}>Letter Details</h3>
            <div className="space-y-4">
              <div>
                <Label className="label-text">Relationship</Label>
                <Select value={relationship} onValueChange={setRelationship}>
                  <SelectTrigger className="mt-2 border-[#E5E0D8]" data-testid="relationship-select">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mom">Mom</SelectItem>
                    <SelectItem value="dad">Dad</SelectItem>
                    <SelectItem value="sister">Sister</SelectItem>
                    <SelectItem value="brother">Brother</SelectItem>
                    <SelectItem value="wife">Wife</SelectItem>
                    <SelectItem value="husband">Husband</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="friend">Friend</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="label-text">Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-2 border-[#E5E0D8]" data-testid="tone-select">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supportive">Supportive</SelectItem>
                    <SelectItem value="loving">Loving</SelectItem>
                    <SelectItem value="funny">Funny</SelectItem>
                    <SelectItem value="hopeful">Hopeful</SelectItem>
                    <SelectItem value="encouraging">Encouraging</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="occasion" className="label-text">Occasion (Optional)</Label>
                <Input
                  id="occasion"
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  placeholder="e.g., Birthday, Holiday, Just Because"
                  className="mt-2 border-[#E5E0D8]"
                  data-testid="occasion-input"
                />
              </div>

              <div>
                <Label htmlFor="life-updates" className="label-text">Life Updates & Memories</Label>
                <Textarea
                  id="life-updates"
                  value={lifeUpdates}
                  onChange={(e) => setLifeUpdates(e.target.value)}
                  placeholder="Recent updates, memories, or things you want to share..."
                  className="mt-2 border-[#E5E0D8] min-h-[100px]"
                  data-testid="life-updates-input"
                />
              </div>

              <div>
                <Label htmlFor="custom" className="label-text">Additional Instructions (Optional)</Label>
                <Textarea
                  id="custom"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Any specific things you want to include?"
                  className="mt-2 border-[#E5E0D8] min-h-[80px]"
                  data-testid="custom-prompt-input"
                />
              </div>

              <Button
                onClick={handleGenerate}
                className="w-full bg-[#B94A3E] hover:bg-[#9d3e33] text-white"
                disabled={generating}
                data-testid="generate-letter-btn"
              >
                {generating ? 'Writing...' : 'Generate Letter Draft'}
              </Button>
            </div>
          </div>

          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <h3 className="text-xl mb-6" style={{fontFamily: 'Spectral, serif'}}>Letter Preview</h3>
            {generatedLetter ? (
              <div className="handwriting text-lg leading-relaxed whitespace-pre-wrap" data-testid="generated-letter-preview">
                {generatedLetter}
              </div>
            ) : (
              <div className="text-center py-12 text-[#737373]" data-testid="no-letter-placeholder">
                <p>Your letter draft will appear here.</p>
                <p className="text-xs mt-2">Draft is saved automatically. Edit and mail from your dashboard.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
